import { Section } from "@aidkitorg/types/lib/legacy/airtable";
import { NavigationNode, ReviewConfiguration } from "@aidkitorg/types/lib/translation/permissions";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { get_deployment, get_rs_host, useAPIPost, useErrorHandler, usePost } from "./API";
import { useAuthorizedCommsChannels } from "./Applicant/Comms";
import { ModularQuestionPage } from "./ModularQuestionPage";
import { StandardMenu, ThreeColumnPage, useApplicantSubtabs } from "./Components/ThreeColumnPage";
import { safeParse, snakeToEnglish, useInterval } from "./Util";
import { ConfigurationContext, PublicConfigurationContext } from "./Context";
import { useLocalizedStrings } from "./Localization";
import { ApplicantIdentities } from "./ApplicantIdentities";
import { captureException } from "@sentry/react";
import { LoggedInConfig } from "@aidkitorg/types/lib/config";
import { Text, interfaceNumber } from "@aidkitorg/types/lib/survey";
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { Icon, Loading } from "@aidkitorg/component-library";
import { ApplicantProvider } from "./Contexts/ApplicantContext";
import { ApplicantPaymentPage } from "./Applicant/PaymentPage";
import { CompileExpressionToJS } from "@aidkitorg/types/lib/translation/expr_to_js";
import { QuickJS } from "@aidkitorg/types/lib/quickJS";
import { useRelatedCases } from "./SupportUtil";
import { evalDistroConditional } from "@aidkitorg/types/lib/eval";
import { ReviewDetails, ReviewWrapper, showV2Review } from "./ReviewWrapper";
import { AdminPanelApplicantPage } from "./AdminPanel/Reviews/AdminPanelApplicantPage";
import { ApplicantPortalPage } from "./ApplicantPortalPage";
import { ApplicantForbiddenPage, ApplicantNotFoundPage } from "./AdminPanel/ErrorPages";

const isApplicantNotFoundError = (error: unknown) => {
  if (typeof error !== 'object' || !error || !('error' in error)) return false;
  const msg = (error as { error: string }).error;
  return msg === 'No Applicant Found' || msg === 'Applicant not found';
};

const isAuthorizationError = (error: unknown) => {
  if (typeof error !== 'object' || !error || !('error' in error)) return false;
  const msg = (error as { error: unknown }).error;
  if (typeof msg !== 'string') return false;
  const lower = msg.toLowerCase();
  return lower.includes('unauthorized') || lower.includes('not authorized') || lower.includes('user not in authorized') || lower.includes('user is not the screener');
};

export function ApplicantPage(props?: {
  readonly?: boolean;
  uid?: string;
  dynamoAppId?: string;
  subsurvey?: string;
  applicantDash?: string;
  portalPath?: string;
  isAdminPanel?: boolean;
  mainContentRenderer?: (props: { uid: string }) => React.ReactNode
}) {
  const params = useParams() as Record<string, string>;
  const uid = props?.uid || params.uid;
  const dynamoAppId = props?.dynamoAppId || params.dynamoAppId;

  const [applicantNotFound, setApplicantNotFound] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const applicantDash = props?.applicantDash || params.applicantDash;
  const portalPath = props?.portalPath || params.portalPath;
  const { hash, pathname, search } = useLocation();
  const [nav, setNav] = useState<NavigationNode[]>([]);

  // Parse URL search params once on mount and seed into initial info (mirrors Apply.tsx).
  const [initialUrlInfo] = useState<Record<string, string>>(() => {
    const urlParams = new URLSearchParams(search);
    const specialKeys = new Set(['key', 'lang', 'view_info', 'back', 'legacy']);
    const result: Record<string, string> = {};
    urlParams.forEach((v, k) => { if (!specialKeys.has(k) && v) result[k] = v; });
    return result;
  });
  const urlInfoSavedStatus = useRef<'not_started' | 'saving' | 'saved'>('not_started');
  const [info, setInfo] = useState<{ [key: string]: any }>(initialUrlInfo);
  const [fetchedInfo, setFetchedInfo] = useState<{ [key: string]: any }>({});
  const [applicantTitle, setApplicantTitle] = useState('');
  const getSurvey = usePost("/survey");
  const getNav = usePost("/navigation");
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const L = useLocalizedStrings();
  const commsChannels = useAuthorizedCommsChannels({ config });
  const getLoggedInConfig = usePost('/program/logged_in_configuration');
  const [loggedInConfig, setLoggedInConfig] = useState<LoggedInConfig | undefined>();
  const [disallowed, setDisallowed] = useState<string[] | null>(null);
  const [applicant, setApplicant] = useState<Awaited<ReturnType<typeof getEverything>> | null>(null);
  const errorHandler = useErrorHandler();
  const getEverything = usePost('/applicant/get_everything', {
    handleErrors: (error, handleToast) => {
      if (isApplicantNotFoundError(error)) {
        setApplicantNotFound(true);
      } else if (isAuthorizationError(error) && props?.isAdminPanel) {
        setAccessDenied(true);
      } else {
        errorHandler(error, handleToast);
      }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const shouldFastForward = useRef<boolean | undefined>();
  const [review, setReview] = useState<ReviewDetails | undefined>(undefined);
  const [subsurvey, setSubsurvey] = useState(props?.subsurvey || params.subsurvey);

  const [surveySections, setSurveySections] = useState<{ sections: Section[] }>({ sections: [] });
  const [activeSections, setActiveSections] = useState<{ sections: Section[] }>({ sections: [] });

  // Finds the NavigationNode for `subsurveyPath`, if any. Pure/synchronous so it can drive a
  // useMemo directly
  const findSubsurveyNode = (navNodes: NavigationNode[], subsurveyPath: string): NavigationNode | null => {
    for (const node of navNodes) {
      // Fall back to matching on href when name has been overridden away from the raw
      // '/p/<path>' routing key (see findFirstSubsurvey below for the same issue).
      const matchesSubsurvey = node.name === '/p/' + subsurveyPath ||
        (node.kind === 'Subsurvey' && node.href?.slice(node.href.lastIndexOf('/') + 1) === subsurveyPath);
      if (matchesSubsurvey && node.subnodes) {
        return node;
      }
      if (node.subnodes) {
        const found = findSubsurveyNode(node.subnodes, subsurveyPath);
        if (found) return found;
      }
    }
    return null;
  };

  const activeSubsurveyNode = useMemo(
    () => (subsurvey && nav.length > 0) ? findSubsurveyNode(nav, subsurvey) : null,
    [nav, subsurvey]
  );

  const isReadonly = useMemo(() => {
    const edits = activeSubsurveyNode?.blockFurtherEdits ?? null;
    if (edits?.block_when && edits.block_users_tagged?.some((role) => config.roles?.includes(role))) {
      return evalDistroConditional(edits.block_when, info || {}) || !!props?.readonly;
    }
    return !!props?.readonly;
  }, [activeSubsurveyNode, config.roles, info, props?.readonly]);

  useAsyncEffect(async () => {
    const applicantId = uid || dynamoAppId;

    const [response, sections] = await Promise.all([
      getNav({ applicant: applicantId }),
      getSurvey({}) as any
    ]);
    
    setNav(response.navigation);
    setSurveySections(sections);
  }, [uid, dynamoAppId]);

  // We prefer to have a subsurvey so that we can follow the complete picture of the rules applied to that subsurvey.
  // Like who should view it, how it should be blocked, etc.
  useAsyncEffect(async () => {
    let subsurveyToRender = props?.subsurvey;
    const applicant = uid || dynamoAppId;

    const findFirstSubsurvey = (nav: NavigationNode[]): string | undefined => {
      for (const node of nav) {
        if (typeof node.name === 'string' && node.name.startsWith('/p/')) {
          return node.name.slice(3);
        }
        // name may have been overridden (e.g. via a navigation override) to a localized
        // display string, losing the raw '/p/<path>' routing key above.
        // Fall back to deriving the path from href.
        if (node.kind === 'Subsurvey' && node.href) {
          const path = node.href.slice(node.href.lastIndexOf('/') + 1);
          if (path) return path;
        }
        if (node.subnodes) {
          const found = findFirstSubsurvey(node.subnodes);
          if (found) {
            return found;
          }
        }
      }
    }

    const url = window.location.href;
    if (!subsurveyToRender) {
      // first, make sure we don't have something else set in the 
      // URL that we want to respect.
      let section = surveySections.sections[surveySections.sections?.findIndex((section: Section) => section["English Content"]?.trim() === decodeURIComponent(hash.slice(1)))];
      if (section) {
        console.log("Section found", section, "returning without a subsurvey");
        setSubsurvey('');
        return;
      }
      
      // subsurvey may have already been set by this useEffect, and might already be in the URL. If it is, we don't want to overwrite it.
      if (subsurvey && window.location.pathname.includes(subsurvey)) return;

      // Don't update URL if this is an applicant dashboard, portal page, or file hub
      if (applicantDash || portalPath) return;

      subsurveyToRender = findFirstSubsurvey(nav) || '';
      if (subsurveyToRender) {
        const newUrl = url.replace(applicant, applicant + '/' + subsurveyToRender);
        window.history.replaceState({}, '', newUrl);
      }
    }

    setSubsurvey(subsurveyToRender || '');
  }, [props?.subsurvey, params.subsurvey, subsurvey, uid, dynamoAppId, nav, window.location.href, surveySections, applicantDash, portalPath]);


  const [fraudFlagsLength, setFraudFlagsLength] = useState<number | null>(null);

  useEffect(() => {
    if (!loggedInConfig) {
      (async () => {
        setLoggedInConfig(await getLoggedInConfig({}));
      })();
    }
  }, [loggedInConfig]);

  const refreshInProgress = useRef<Promise<any> | null>(null)
  const currentApplicantId = useRef(uid || dynamoAppId);

  async function refreshApplicant(extras?: Parameters<typeof getEverything>[0]["extras"]) {
    if (refreshInProgress.current) return;

    let app;
    try {
      const applicantId = currentApplicantId.current;
      refreshInProgress.current = getEverything({ uid: applicantId, extras });
      app = await refreshInProgress.current;

      if (currentApplicantId.current !== applicantId) {
        // This info is for the wrong applicant
        return;
      }

      if (app && app.ingestedUid) {
        const appPath = interfaceNumber(publicConfig?.interface?.version) >= 1 ? '/a/' : '/applicant/';
        window.location.href = appPath + app.ingestedUid + search;
      }
      if (app?.defaultApplicantPage && !hash.length && pathname.match(new RegExp(`^(\/admin-panel)?\/a\/${uid}\/?$`))) {
        const basePath = props?.isAdminPanel ? `/admin-panel/a/${uid}/` : `/a/${uid}/`;
        window.location.href = basePath + app.defaultApplicantPage + search;
      }
    } finally {
      refreshInProgress.current = null;
    }

    // We may have changed things since in the await — but only stall the merge if the
    // pending edits actually belong to the applicant we just fetched. Otherwise the bag
    // is for a different applicant and shouldn't block this one's refresh.
    const applicantIdAfter = currentApplicantId.current;
    if (
      pendingChangesUid.current === applicantIdAfter &&
      Object.keys(pendingChanges.current).length > 0
    ) {
      return;
    }

    if (app) {
      setInfo(app.info || {});
      setFetchedInfo(app.info || {});
      setApplicant(app);
    }
  }
  useEffect(() => {
    refreshApplicant({ defaultApplicantPage: true });
  }, [])

  // Once the first server fetch lands, merge URL params into the full server snapshot,
  // save them via saveInfo
  useEffect(() => {
    if (urlInfoSavedStatus.current !== 'not_started' || Object.keys(initialUrlInfo).length === 0) return;
    if (Object.keys(fetchedInfo).length === 0) return;
    urlInfoSavedStatus.current = 'saving';
    saveInfo({ ...fetchedInfo, ...initialUrlInfo });
  }, [fetchedInfo]);

  useEffect(() => {
    currentApplicantId.current = uid || dynamoAppId;
  }, [uid, dynamoAppId]);

  useAsyncEffect(async () => {
    if (surveySections.sections.length === 0) {
      return;
    }

    // Required for determining if we are showing the new V2 review mode.
    if (!config.user?.email) {
      return;
    }

    setIsLoading(true);
    setReview(undefined);
    shouldFastForward.current = undefined;

    // at this point, if we don't have a subsurvey, we are never going to get one. 
    // so just find the matching section (or the first one) and render it.
    if (!subsurvey || nav.length === 0) {
      let section = surveySections.sections[surveySections.sections?.findIndex((section: Section) => section["English Content"]?.trim() === decodeURIComponent(hash.slice(1)))];
      const activeSections = [];
      if (!section && surveySections.sections.length > 0) {
        console.log("No section found, setting to first section", surveySections.sections[0]);
        section = surveySections.sections[0];
      }
      if (section) {
        activeSections.push(section);
        setActiveSections({ sections: activeSections });
      };
      setIsLoading(false);
      return;
    }

    // Reuse the same node lookup that drives isReadonly, rather than re-traversing nav here.
    const node = activeSubsurveyNode;
    shouldFastForward.current = node?.shouldFastForward;
    if (node?.reviewConfiguration && showV2Review(node.reviewConfiguration, config.user?.email || '')) {
      setReview({
        ...node.reviewConfiguration,
        title: node.subsurveyTitle || snakeToEnglish(subsurvey),
      } satisfies ReviewDetails);
    }

    const subsurveysections = node?.subnodes || null;
    const activeSections = [];
    for (const section of subsurveysections || []) {
      const c = surveySections.sections.filter((s: Section) => s["English Content"] === section.name || s["English Content"] === (section.name as Text).en);

      if (c.length === 1) {
        activeSections.push(c[0])
      } else if (c.length > 0) {
        // Perform string matching on path injected into sectionIdentifier in order to differentiate between sections with the same title
        const sectionPath = section.sectionPath
        let matchedContent: any[] = []

        if (sectionPath && sectionPath !== "") {
          // The Subsurvey kind in matched content should have the path for the subsurvey
          matchedContent = c.filter((s: Section) => s["Questions"].find((q) => (q["Kind"] === 'Subsurvey' && q["Question"] === sectionPath)))
        }

        // If none of this works, default to pushing the first content found
        if (matchedContent.length > 0) {
          activeSections.push(matchedContent[0])
        } else {
          activeSections.push(c[0])
        }
      }
    }

    if (!activeSections.length && subsurveysections?.[0]) {
      // If we don't have any active sections, set the first section as active
      console.log("No active sections, setting to first section", subsurveysections?.[0]);
      activeSections.push(subsurveysections?.[0]);
    }
    console.log("Active sections", activeSections);
    setActiveSections({ sections: activeSections });
    setIsLoading(false);
  }, [subsurvey, uid, dynamoAppId, hash, nav, surveySections, config.user?.email, activeSubsurveyNode]);

  useEffect(() => {
    // temporary convention till we can implement explicit RBAC.
    // this scoops up all "no-[name]" tags and lets us use em 
    // in the tab array below to more easily control what is shown.
    //
    // TODO: Remove this once we have a better RBAC!
    if (config.roles) {
      setDisallowed(Array.from((config.roles || []) as string[]).filter((r) => r.startsWith('no-')).map(r => r.replace('no-', '')));
    }
  }, [config.roles]);

  useAsyncEffect(async () => {
    const defaultValue = info["legal_name"] || "Applicant";
    if (!publicConfig?.interface?.applicantDisplayName) {
      setApplicantTitle(defaultValue);
      return;
    }

    try {
      const js = CompileExpressionToJS(publicConfig.interface.applicantDisplayName.valueExpr);
      setApplicantTitle(await QuickJS.compute(js, { info }));
    } catch (err) {
      captureException('Error computing applicant display name', { extra: { err, uid, deployment: get_deployment() } })
      setApplicantTitle(defaultValue);
    }
  }, [publicConfig, info]);

  // We store a ref of pending changes to prevent race conditions between other callbacks
  // that might otherwise close around this state. Pending changes are scoped to the applicant
  // they were produced for so a navigation A->B can't merge A's edits into a save against B
  // (invariant #3: no cross-applicant writes).
  const pendingChanges = useRef<Record<string, any>>({})
  const pendingChangesUid = useRef<string | undefined>(undefined)
  const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveInfoRS = useAPIPost(get_rs_host() + "/compute_and_save", { includeTokenInData: true });

  const cleanupUrlParams = () => {
    if (urlInfoSavedStatus.current === 'saving') {
      urlInfoSavedStatus.current = 'saved';
      const urlParams = new URLSearchParams(search);
      Object.keys(initialUrlInfo).forEach(k => urlParams.delete(k));
      const newSearch = urlParams.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash);
    }
  }

  // The applicant_uid this render is scoped to — used by saveInfo to tag pending
  // edits and by the polling refresh below to decide whether to stall.
  const currentUid = uid || dynamoAppId;

  function saveInfo(update: Record<string, any>) {
    if (isReadonly) return;

    // Figure out what changed (note, we know that "deleted" keys are set as '' so we don't
    // need to search for keys that have disappeared)
    setInfo(update);
    const changes = Object.keys(update).reduce((prev, k: string) => {
      if (update[k] !== info[k]) {
        prev[k] = update[k];
      }
      return prev;
    }, {} as Record<string, any>)

    if (Object.keys(changes).length) {
      // If pending changes belong to a different applicant (e.g. user navigated A->B before
      // the debounce fired), flush them eagerly under their original uid before claiming
      // the bag for this applicant. Never let one applicant's edits land on another.
      if (pendingChangesUid.current && pendingChangesUid.current !== currentUid) {
        if (pendingTimeout.current) {
          clearTimeout(pendingTimeout.current);
          pendingTimeout.current = null;
        }
        const staleChanges = { ...pendingChanges.current };
        const staleUid = pendingChangesUid.current;
        pendingChanges.current = {};
        pendingChangesUid.current = undefined;
        if (Object.keys(staleChanges).length > 0) {
          saveInfoRS({
            changedKeys: staleChanges,
            uid: staleUid,
            deploymentKey: get_deployment(),
          }).catch(err =>
            captureException(err, { extra: { uid: staleUid, deploymentKey: get_deployment() } })
          );
        }
      }

      // Update any pending changes (now guaranteed to be for currentUid)
      pendingChangesUid.current = currentUid;
      Object.assign(pendingChanges.current, changes);

      // Debounce saving by 1 second
      if (pendingTimeout.current) {
        clearTimeout(pendingTimeout.current)
      }
      pendingTimeout.current = setTimeout(async () => {
        // Guard: if the bag was reclaimed for another applicant in between, this
        // scheduled save is no longer authoritative — let the new owner's flush handle it.
        if (pendingChangesUid.current !== currentUid) return;

        const toSave = { ...pendingChanges.current };

        const result = await saveInfoRS({
          changedKeys: toSave,
          uid: currentUid,
          deploymentKey: get_deployment()
        })
        if (!result) {
          captureException("Error saving info", {
            extra: {
              uid: currentUid,
              program: get_deployment(),
              keys: Object.keys(toSave)
            }})
        }

        // We need to delete the keys that we attempted to save
        // even if there was an error else this while loop will
        // never terminate
        if (result?.error) {
          console.error("Error saving", result.error, Object.keys(pendingChanges.current).join(","));
          captureException(result.error, { extra: { uid: currentUid, deploymentKey: get_deployment() } });
        }
        // Only mutate the pending bag if it still belongs to the applicant we just saved for.
        if (pendingChangesUid.current === currentUid) {
          for (const k of Object.keys(toSave)) {
            if (pendingChanges.current[k] === toSave[k]) {
              delete pendingChanges.current[k];
            }
          }
          if (Object.keys(pendingChanges.current).length === 0) {
            pendingChangesUid.current = undefined;
          }
        }

        // Merge compute_and_save side effects into local info, skipping fields the user
        // is still editing. Bail if currentApplicantId.current no longer matches currentUid
        // (the user navigated to a different applicant). Don't compare against uid —
        // uid was captured when saveInfo ran, so it equals currentUid by definition.
        if (result && 'value' in result && currentApplicantId.current === currentUid) {
          const serverUpdates = result.value as Record<string, string>;
          setInfo(prev => {
            const safeUpdates = Object.keys(serverUpdates).reduce((acc, k) => {
              if (!(k in pendingChanges.current)) {
                acc[k] = serverUpdates[k];
              }
              return acc;
            }, {} as Record<string, string>);
            return Object.keys(safeUpdates).length > 0 ? { ...prev, ...safeUpdates } : prev;
          });
          cleanupUrlParams();
        }

        // TODO: eventually remove this! We are putting this in to allow BRF RS calls to take longer without erroring.
        // We would like to remove this once we have made smarter changes to RS.
      }, get_deployment() === 'brf' ? 1500 : 1000);
    } else {
      cleanupUrlParams();
    }
  }

  // Refresh every 3 seconds unless there are pending changes or the applicant does not exist
  useInterval(() => {
    if (applicantNotFound || accessDenied) {
      return;
    }

    // Only skip the refresh when the pending edits are for the applicant currently on screen;
    // a stale bag for a previous applicant shouldn't stall the current view.
    if (
      pendingChangesUid.current === currentUid &&
      Object.keys(pendingChanges.current).length > 0
    ) {
      console.log("Pending changes", pendingChanges.current)
      return;
    }
    refreshApplicant();
  }, 3000);

  const relatedSupportCases = useRelatedCases(uid || dynamoAppId);

  const applicantContext = useMemo(() => ({
    info,
    update: saveInfo,
  }), [info, saveInfo]);

  const subtabs = useApplicantSubtabs({
    uid,
    disallowed,
    commsChannels,
    fetchedInfo,
    loggedInConfig,
    fraudFlagsLength,
    setFraudFlagsLength,
    relatedSupportCases,
    roles: config.roles,
    publicConfig,
    saveInfo,
    dynamoAppId,
    info,
  });

  if (accessDenied && props?.isAdminPanel) {
    return <ApplicantForbiddenPage />;
  }

  if (applicantNotFound) {
    if (props?.isAdminPanel) {
      return <ApplicantNotFoundPage />;
    }
    return <ThreeColumnPage
      main={<div className="flex flex-col items-center justify-center h-full">
        <Icon name='ExclamationTriangleIcon' type='solid' className='text-yellow-800 h-14 w-14' />
        <h3 className="text-center">{L.applicant_not_found}</h3>
      </div>}
    />;
  }

  async function checkpoint(params: { info: Record<string, string | undefined> }) {
    if (isReadonly || !currentUid) return;
    if (pendingTimeout.current) {
      clearTimeout(pendingTimeout.current);
      pendingTimeout.current = null;
    }
    const changedKeys = { ...pendingChanges.current };
    for (const [k, v] of Object.entries(params.info)) {
      if (v !== info[k]) changedKeys[k] = v;
    }
    pendingChanges.current = {};
    pendingChangesUid.current = undefined;
    return saveInfoRS({ changedKeys, uid: currentUid, deploymentKey: get_deployment() });
  }

  // Common props for ModularQuestionPage
  const modularQuestionPageProps = {
    sections: activeSections as any,
    uid: uid,
    viewInfo: { readonly: isReadonly ? 'true' : undefined } as const,
    submit: async () => { },
    checkpoint: publicConfig?.experimental?.livenessSameTab ? checkpoint : undefined,
    blockFurtherEdits: activeSubsurveyNode?.blockFurtherEdits || undefined,
    shouldFastForward: shouldFastForward.current === undefined ? undefined : shouldFastForward,
    noNavBar: subsurvey ? undefined : true as true,
    hideLogoAndLanguage: props?.isAdminPanel ? true as true : undefined,
    sequential: true,
    Viewer: 'screener' as const,
    review: review,
  };

  // Determine which main content to render
  const renderMainContent = () => {
    if (props?.mainContentRenderer) return props.mainContentRenderer({ uid });
    
    const pathToCheck = pathname.replace('/admin-panel/', '/');
    if (pathToCheck.startsWith('/payments/')) {
      return <ApplicantPaymentPage applicant={uid} />;
    }

    if (applicantDash) {
      return <ApplicantIdentities applicant={uid} path={applicantDash} />;
    }

    if (portalPath) {
      return <ApplicantPortalPage Viewer="screener" uid={uid} portalPath={portalPath} embedded />;
    }

    if (review) {
      return (
        <ReviewWrapper>
          <ModularQuestionPage {...modularQuestionPageProps} />
        </ReviewWrapper>
      );
    }

    return <ModularQuestionPage {...modularQuestionPageProps} />;
  };

  if (props?.isAdminPanel) {
    return (
      <AdminPanelApplicantPage
        uid={uid}
        dynamoAppId={dynamoAppId}
        review={review}
        isLoading={isLoading}
        renderMainContent={renderMainContent}
        nav={nav}
        subsurvey={subsurvey}
        setSubsurvey={setSubsurvey}
        info={info}
        applicantTitle={applicantTitle}
        applicantContext={applicantContext}
        disallowed={disallowed}
        fetchedInfo={fetchedInfo}
        loggedInConfig={loggedInConfig}
        fraudFlagsLength={fraudFlagsLength}
        setFraudFlagsLength={setFraudFlagsLength}
        relatedSupportCases={relatedSupportCases}
        roles={config.roles}
        publicConfig={publicConfig}
        saveInfo={saveInfo}
        commsChannels={commsChannels}
      />
    );
  }
  
  return <ApplicantProvider context={applicantContext}>
    <ThreeColumnPage
      nav={isLoading ? [] : nav}
      review={review}
      title={applicantTitle}
      defaultSubtabKey="notes"
      main={<Loading loading={isLoading}>
        {renderMainContent()}
      </Loading>}
      subtabs={subtabs}
    />
  </ApplicantProvider>
}
